/**
 * AdminWorshipHub — Phase W1 foundation.
 *
 * Route: /admin/worship
 *
 * Tabs:
 *   • Songs — searchable library + create/edit dialog
 *   • Sets  — reusable worship sets (drag-reorder pending Phase W2)
 *   • Services — full service planner with block editor
 *
 * `Present` on any service opens a full-screen slide viewer that
 * consumes `GET /admin/worship/services/{id}/present` in one shot.
 */
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import {
  ArrowLeft, Music, ListMusic, Calendar, Search, PlusCircle,
  Play, X, Users,
} from 'lucide-react';
import { getToken } from '@/lib/authToken';

import VolunteerAssignDialog, { NotifyTeamButton } from '@/pages/worship/VolunteerAssignDialog';
import AiSetAssistantDialog from '@/pages/worship/AiSetAssistantDialog';
import AiSongSearchDialog from '@/pages/worship/AiSongSearchDialog';
import RehearsalsTab from '@/pages/worship/RehearsalsTab';
import { LeadSheetUpload } from '@/pages/worship/presenter/LeadSheetViewer';

const API = process.env.REACT_APP_BACKEND_URL;
const authH = () => ({ Authorization: `Bearer ${getToken()}` });
const W = `${API}/api/admin/worship`;

export default function AdminWorshipHub() {
  const navigate = useNavigate();
  const [tab, setTab] = useState('songs');

  const dashQ = useQuery({
    queryKey: ['worship-dash'],
    queryFn: async () => (await axios.get(`${W}/dashboard`, { headers: authH() })).data,
  });

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100" data-testid="admin-worship-page">
      <div className="mx-auto max-w-6xl space-y-4 p-4 sm:p-6">
        <div>
          <Button variant="ghost" size="sm" className="mb-1 text-slate-400 hover:text-slate-100" onClick={() => navigate(-1)}>
            <ArrowLeft className="mr-1 h-4 w-4" /> Back
          </Button>
          <h1 className="flex items-center gap-2 text-2xl font-bold">
            <Music className="h-6 w-6 text-violet-400" /> Faith Works Worship
          </h1>
          <p className="text-xs text-slate-500">
            Songs · Worship sets · Service planning · Live presentation
          </p>
        </div>

        {/* Dashboard tiles */}
        <div className="grid grid-cols-2 gap-2 md:grid-cols-4">
          <Tile testid="tile-songs" label="Songs" value={dashQ.data?.songs_total || 0} icon={Music} tone="violet" />
          <Tile testid="tile-sets" label="Worship Sets" value={dashQ.data?.sets_total || 0} icon={ListMusic} tone="emerald" />
          <Tile testid="tile-services" label="Services" value={dashQ.data?.services_total || 0} icon={Calendar} tone="sky" />
          <Tile testid="tile-upcoming" label="Upcoming" value={(dashQ.data?.upcoming_services || []).length} icon={Play} tone="amber" />
        </div>

        <div className="flex flex-wrap gap-2">
          {[
            { id: 'songs', label: 'Songs', icon: Music },
            { id: 'sets', label: 'Worship Sets', icon: ListMusic },
            { id: 'services', label: 'Services', icon: Calendar },
            { id: 'rehearsals', label: 'Rehearsals', icon: Users },
          ].map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              type="button"
              data-testid={`worship-tab-${id}`}
              onClick={() => setTab(id)}
              className={`flex items-center gap-1 rounded-full border px-3 py-1 text-xs ${
                tab === id
                  ? 'border-violet-500 bg-violet-500/15 text-violet-200'
                  : 'border-slate-800 bg-slate-950 text-slate-400 hover:border-slate-700 hover:text-slate-200'
              }`}
            >
              <Icon className="h-3 w-3" /> {label}
            </button>
          ))}
        </div>

        {tab === 'songs' && <SongsTab />}
        {tab === 'sets' && <SetsTab />}
        {tab === 'services' && <ServicesTab onPresent={(id) => navigate(`/worship/present/${id}`)} />}
        {tab === 'rehearsals' && <RehearsalsTab />}
      </div>
    </div>
  );
}


function Tile({ label, value, icon: Icon, tone, testid }) {
  const toneClass = {
    violet: 'border-violet-500/30 bg-violet-500/5 text-violet-300',
    emerald: 'border-emerald-500/30 bg-emerald-500/5 text-emerald-300',
    sky: 'border-sky-500/30 bg-sky-500/5 text-sky-300',
    amber: 'border-amber-500/30 bg-amber-500/5 text-amber-300',
  }[tone];
  return (
    <div data-testid={testid} className={`rounded-lg border p-3 ${toneClass}`}>
      <div className="flex items-center justify-between">
        <span className="text-[10px] uppercase tracking-wider opacity-80">{label}</span>
        <Icon className="h-3.5 w-3.5" />
      </div>
      <div className="mt-1 text-2xl font-bold text-slate-100">{value}</div>
    </div>
  );
}


// ═══ SONGS TAB ═══
function SongsTab() {
  const qc = useQueryClient();
  const [q, setQ] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [aiSearchOpen, setAiSearchOpen] = useState(false);

  const songsQ = useQuery({
    queryKey: ['worship-songs', q],
    queryFn: async () => (await axios.get(
      `${W}/songs${q ? `?q=${encodeURIComponent(q)}` : ''}`,
      { headers: authH() },
    )).data,
  });

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
          <Input data-testid="song-search" value={q} onChange={(e) => setQ(e.target.value)}
                 placeholder="Search songs by title, lyric, tag…"
                 className="pl-8 border-slate-700 bg-slate-900" />
        </div>
        <Button data-testid="ai-song-search-btn" variant="outline"
                className="border-emerald-500/40 bg-emerald-500/10 text-emerald-200 hover:bg-emerald-500/20"
                onClick={() => setAiSearchOpen(true)}>
          ✨ AI Search
        </Button>
        <Button data-testid="new-song-btn" className="bg-violet-600 hover:bg-violet-500" onClick={() => setCreateOpen(true)}>
          <PlusCircle className="mr-1 h-4 w-4" /> New Song
        </Button>
      </div>

      <Card className="border-slate-800 bg-slate-900/60">
        <CardContent className="pt-4 max-h-[65vh] overflow-y-auto">
          {songsQ.isLoading && <p className="text-xs text-slate-500">Loading…</p>}
          <div className="space-y-1">
            {(songsQ.data?.items || []).map((s) => (
              <SongRow key={s.id} song={s} />
            ))}
            {(songsQ.data?.items || []).length === 0 && !songsQ.isLoading && (
              <p className="text-center text-xs text-slate-500">No songs yet — click New Song to start your library.</p>
            )}
          </div>
        </CardContent>
      </Card>

      {createOpen && (
        <CreateSongDialog onClose={() => setCreateOpen(false)} onCreated={() => {
          qc.invalidateQueries({ queryKey: ['worship-songs'] });
          qc.invalidateQueries({ queryKey: ['worship-dash'] });
          setCreateOpen(false);
        }} />
      )}
      {aiSearchOpen && <AiSongSearchDialog onClose={() => setAiSearchOpen(false)} />}
    </div>
  );
}


function SongRow({ song }) {
  const qc = useQueryClient();
  const [showSheet, setShowSheet] = useState(false);
  return (
    <div data-testid={`song-row-${song.id}`}
         className="rounded border border-slate-800 bg-slate-950 px-3 py-2 text-sm">
      <div className="flex items-center justify-between">
        <div>
          <div className="font-medium text-slate-100">{song.title}</div>
          <div className="text-[10px] text-slate-500">
            {song.author || '—'} · key {song.key || '—'} · {song.language} · used {song.usage_count}×
            {song.lead_sheet_url && <span className="ml-2 text-amber-300">· sheet ✓</span>}
          </div>
        </div>
        <div className="flex items-center gap-1">
          {(song.themes || []).slice(0, 2).map((t) => (
            <Badge key={t} variant="outline" className="border-violet-500/30 text-violet-300 text-[10px]">{t}</Badge>
          ))}
          <button
            type="button"
            data-testid={`song-sheet-${song.id}`}
            onClick={() => setShowSheet((v) => !v)}
            className="rounded border border-amber-500/40 bg-amber-500/10 px-1.5 py-0.5 text-[10px] text-amber-200 hover:bg-amber-500/20"
          >
            Sheet
          </button>
        </div>
      </div>
      {showSheet && (
        <div className="mt-2">
          <LeadSheetUpload
            songId={song.id}
            currentUrl={song.lead_sheet_url}
            onChange={() => qc.invalidateQueries({ queryKey: ['worship-songs'] })}
          />
        </div>
      )}
    </div>
  );
}


function CreateSongDialog({ onClose, onCreated }) {
  const [title, setTitle] = useState('');
  const [author, setAuthor] = useState('');
  const [song_key, setKey] = useState('');
  const [themes, setThemes] = useState('');
  const [lyrics, setLyrics] = useState('');

  const create = useMutation({
    mutationFn: async () => (await axios.post(
      `${W}/songs`,
      { title, author, key: song_key,
        themes: themes.split(',').map((t) => t.trim()).filter(Boolean),
        lyrics },
      { headers: { ...authH(), 'Content-Type': 'application/json' } },
    )).data,
    onSuccess: () => { toast.success('Song added'); onCreated?.(); },
    onError: (e) => toast.error(`Failed: ${e?.response?.data?.detail || e.message}`),
  });

  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent data-testid="song-dialog" className="bg-slate-900 border-slate-700 text-slate-100 max-h-[85vh] overflow-y-auto">
        <DialogHeader><DialogTitle>New Song</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <Input data-testid="song-title" placeholder="Song title" value={title} onChange={(e) => setTitle(e.target.value)} className="bg-slate-950 border-slate-700" />
          <div className="grid grid-cols-2 gap-2">
            <Input data-testid="song-author" placeholder="Author" value={author} onChange={(e) => setAuthor(e.target.value)} className="bg-slate-950 border-slate-700" />
            <Input data-testid="song-key" placeholder="Key (e.g. G, C, Bb)" value={song_key} onChange={(e) => setKey(e.target.value)} className="bg-slate-950 border-slate-700" />
          </div>
          <Input data-testid="song-themes" placeholder="Themes (comma-separated: worship, faith, salvation)" value={themes} onChange={(e) => setThemes(e.target.value)} className="bg-slate-950 border-slate-700" />
          <Textarea data-testid="song-lyrics" placeholder="Lyrics (one line per line — verse breaks with blank line)" value={lyrics} onChange={(e) => setLyrics(e.target.value)} className="bg-slate-950 border-slate-700 min-h-[200px]" />
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={onClose}>Cancel</Button>
            <Button data-testid="song-submit" className="bg-violet-600 hover:bg-violet-500"
                    onClick={() => create.mutate()} disabled={!title.trim() || create.isPending}>
              {create.isPending ? 'Saving…' : 'Save Song'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}


// ═══ SETS TAB ═══
function SetsTab() {
  const setsQ = useQuery({
    queryKey: ['worship-sets'],
    queryFn: async () => (await axios.get(`${W}/sets`, { headers: authH() })).data,
  });
  return (
    <Card className="border-slate-800 bg-slate-900/60">
      <CardHeader className="pb-3"><CardTitle className="text-base flex items-center gap-2"><ListMusic className="h-4 w-4 text-emerald-300" /> Worship Sets</CardTitle></CardHeader>
      <CardContent className="max-h-[65vh] overflow-y-auto">
        {(setsQ.data?.items || []).length === 0 && !setsQ.isLoading && (
          <p className="text-xs text-slate-500 text-center">
            Sets group songs for a specific context (Sunday Morning · Communion · Christmas). Create your first set from the Services tab by picking a service and choosing &ldquo;Save as reusable set&rdquo;.
          </p>
        )}
        <div className="space-y-1">
          {(setsQ.data?.items || []).map((s) => (
            <div key={s.id} data-testid={`set-row-${s.id}`} className="rounded border border-slate-800 bg-slate-950 px-3 py-2 text-sm">
              <div className="font-medium text-slate-100">{s.name}</div>
              <div className="text-[10px] text-slate-500">{s.song_count} songs · {s.category || '—'}</div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}


// ═══ SERVICES TAB ═══
function ServicesTab({ onPresent }) {
  const qc = useQueryClient();
  const [createOpen, setCreateOpen] = useState(false);

  const servicesQ = useQuery({
    queryKey: ['worship-services'],
    queryFn: async () => (await axios.get(`${W}/services`, { headers: authH() })).data,
  });

  return (
    <div className="space-y-3">
      <div className="flex justify-end">
        <Button data-testid="new-service-btn" className="bg-sky-600 hover:bg-sky-500" onClick={() => setCreateOpen(true)}>
          <PlusCircle className="mr-1 h-4 w-4" /> New Service
        </Button>
      </div>
      <Card className="border-slate-800 bg-slate-900/60">
        <CardContent className="pt-4 max-h-[65vh] overflow-y-auto">
          {(servicesQ.data?.items || []).length === 0 && !servicesQ.isLoading && (
            <p className="text-center text-xs text-slate-500">No services planned yet.</p>
          )}
          <div className="space-y-1">
            {(servicesQ.data?.items || []).map((s) => (
              <ServiceRow key={s.id} service={s} onPresent={onPresent} />
            ))}
          </div>
        </CardContent>
      </Card>

      {createOpen && (
        <CreateServiceDialog onClose={() => setCreateOpen(false)} onCreated={() => {
          qc.invalidateQueries({ queryKey: ['worship-services'] });
          qc.invalidateQueries({ queryKey: ['worship-dash'] });
          setCreateOpen(false);
        }} />
      )}
    </div>
  );
}



function ServiceRow({ service: s, onPresent }) {
  const qc = useQueryClient();
  const publish = useMutation({
    mutationFn: async () => (await axios.put(
      `${W}/services/${s.id}/publish`,
      { is_public: !s.is_public },
      { headers: { ...authH(), 'Content-Type': 'application/json' } },
    )).data,
    onSuccess: (d) => {
      toast.success(d.is_public ? 'Service published — link ready to share' : 'Service unpublished');
      qc.invalidateQueries({ queryKey: ['worship-services'] });
    },
    onError: (e) => toast.error(`Failed: ${e?.response?.data?.detail || e.message}`),
  });
  const copyLink = () => {
    const url = `${window.location.origin}/portal/service/${s.id}`;
    try { navigator.clipboard.writeText(url); toast.success('Portal link copied'); }
    catch (e) { toast.error('Copy failed'); }
  };
  return (
    <div data-testid={`service-row-${s.id}`}
         className="rounded border border-slate-800 bg-slate-950 px-3 py-2 text-sm flex flex-wrap items-center justify-between gap-2">
      <div className="min-w-0">
        <div className="flex items-center gap-2">
          <span className="font-medium text-slate-100">{s.name}</span>
          {s.is_public && (
            <Badge variant="outline" className="border-emerald-500/40 text-emerald-200 text-[9px]">public</Badge>
          )}
        </div>
        <div className="text-[10px] text-slate-500">
          {s.block_count} blocks · {s.speaker || 'no speaker'}
          {s.scheduled_at ? ` · ${new Date(s.scheduled_at).toLocaleString()}` : ''}
        </div>
      </div>
      <div className="flex items-center gap-1">
        <button type="button"
                data-testid={`publish-${s.id}`}
                onClick={() => publish.mutate()}
                disabled={publish.isPending}
                className={`rounded border px-2 py-1 text-[10px] ${
                  s.is_public
                    ? 'border-emerald-500/40 bg-emerald-500/10 text-emerald-200 hover:bg-emerald-500/20'
                    : 'border-slate-700 bg-slate-950 text-slate-300 hover:border-slate-500'
                }`}>
          {s.is_public ? 'Unpublish' : 'Publish'}
        </button>
        {s.is_public && (
          <button type="button" data-testid={`copy-portal-${s.id}`} onClick={copyLink}
                  className="rounded border border-emerald-500/40 bg-emerald-500/10 px-2 py-1 text-[10px] text-emerald-200 hover:bg-emerald-500/20">
            Share
          </button>
        )}
        <NotifyTeamButton serviceId={s.id} />
        <Button size="sm" data-testid={`present-${s.id}`}
                className="bg-violet-600 hover:bg-violet-500" onClick={() => onPresent(s.id)}>
          <Play className="mr-1 h-3 w-3" /> Present
        </Button>
      </div>
    </div>
  );
}


function CreateServiceDialog({ onClose, onCreated }) {
  const [name, setName] = useState('');
  const [speaker, setSpeaker] = useState('');
  const [theme, setTheme] = useState('');
  const [blocks, setBlocks] = useState([
    { type: 'countdown', title: 'Countdown', duration_min: 5 },
    { type: 'welcome', title: 'Welcome' },
  ]);
  const [aiOpen, setAiOpen] = useState(false);

  const songsQ = useQuery({
    queryKey: ['worship-songs-for-service'],
    queryFn: async () => (await axios.get(`${W}/songs?limit=200`, { headers: authH() })).data,
  });

  const addBlock = (type) => {
    setBlocks((prev) => [...prev, { type, title: type.replace('_', ' ').replace(/\b\w/g, (l) => l.toUpperCase()) }]);
  };

  const addSongBlockFromSuggestion = (s) => {
    setBlocks((prev) => [...prev, { type: 'song', title: s.title, song_id: s.song_id }]);
  };

  const currentSongIds = blocks.filter((b) => b.type === 'song' && b.song_id).map((b) => b.song_id);

  const updateBlock = (i, patch) => {
    setBlocks((prev) => prev.map((b, idx) => idx === i ? { ...b, ...patch } : b));
  };

  const removeBlock = (i) => setBlocks((prev) => prev.filter((_, idx) => idx !== i));

  const create = useMutation({
    mutationFn: async () => (await axios.post(
      `${W}/services`,
      { name, speaker, theme, blocks },
      { headers: { ...authH(), 'Content-Type': 'application/json' } },
    )).data,
    onSuccess: () => { toast.success('Service created'); onCreated?.(); },
    onError: (e) => toast.error(`Failed: ${e?.response?.data?.detail || e.message}`),
  });

  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent data-testid="service-dialog" className="bg-slate-900 border-slate-700 text-slate-100 max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader><DialogTitle>New Service Plan</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <Input data-testid="service-name" placeholder="Service name (e.g. Sunday 10am)" value={name} onChange={(e) => setName(e.target.value)} className="bg-slate-950 border-slate-700" />
          <div className="grid grid-cols-2 gap-2">
            <Input data-testid="service-speaker" placeholder="Speaker" value={speaker} onChange={(e) => setSpeaker(e.target.value)} className="bg-slate-950 border-slate-700" />
            <Input data-testid="service-theme" placeholder="Theme" value={theme} onChange={(e) => setTheme(e.target.value)} className="bg-slate-950 border-slate-700" />
          </div>

          <div>
            <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Blocks</div>
            <div className="space-y-1">
              {blocks.map((b, i) => (
                <div key={i} className="rounded border border-slate-800 bg-slate-950 p-2 text-xs space-y-1" data-testid={`block-${i}`}>
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="border-slate-700 text-slate-300 text-[10px]">{b.type}</Badge>
                    <Input value={b.title || ''} onChange={(e) => updateBlock(i, { title: e.target.value })} placeholder="Title" className="h-7 bg-slate-900 border-slate-700 flex-1 text-xs" />
                    <button type="button" onClick={() => removeBlock(i)} className="text-slate-500 hover:text-rose-400"><X className="h-3 w-3" /></button>
                  </div>
                  {b.type === 'song' && (
                    <select data-testid={`block-song-${i}`} value={b.song_id || ''} onChange={(e) => updateBlock(i, { song_id: e.target.value })} className="w-full rounded border border-slate-700 bg-slate-900 px-2 py-1 text-xs">
                      <option value="">— pick song —</option>
                      {(songsQ.data?.items || []).map((s) => <option key={s.id} value={s.id}>{s.title}</option>)}
                    </select>
                  )}
                  {b.type === 'scripture' && (
                    <Input value={b.scripture_ref || ''} onChange={(e) => updateBlock(i, { scripture_ref: e.target.value })} placeholder="e.g. John 3:16 or Psalm 23:1-4" className="h-7 bg-slate-900 border-slate-700 text-xs" />
                  )}
                  {(b.type === 'announcement' || b.type === 'sermon' || b.type === 'custom' || b.type === 'welcome' || b.type === 'prayer') && (
                    <Textarea value={b.body || ''} onChange={(e) => updateBlock(i, { body: e.target.value })} placeholder="Body / notes" className="bg-slate-900 border-slate-700 min-h-[40px] text-xs" />
                  )}
                </div>
              ))}
            </div>
            <div className="mt-2 flex flex-wrap gap-1">
              {['countdown', 'welcome', 'prayer', 'song', 'scripture', 'announcement', 'offering', 'sermon', 'ministry_time', 'communion', 'benediction'].map((t) => (
                <button key={t} type="button" onClick={() => addBlock(t)} className="rounded-full border border-slate-700 bg-slate-950 px-2 py-0.5 text-[10px] text-slate-400 hover:border-violet-500 hover:text-violet-300" data-testid={`add-block-${t}`}>+ {t.replace('_', ' ')}</button>
              ))}
              <button
                type="button"
                data-testid="open-ai-assistant"
                onClick={() => setAiOpen(true)}
                className="rounded-full border border-fuchsia-500/40 bg-fuchsia-500/10 px-2 py-0.5 text-[10px] font-medium text-fuchsia-200 hover:border-fuchsia-400"
              >
                ✨ AI Suggest songs
              </button>
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button variant="ghost" onClick={onClose}>Cancel</Button>
            <Button data-testid="service-submit" className="bg-sky-600 hover:bg-sky-500" onClick={() => create.mutate()} disabled={!name.trim() || create.isPending}>
              {create.isPending ? 'Saving…' : 'Create Service'}
            </Button>
          </div>
        </div>
        {aiOpen && (
          <AiSetAssistantDialog
            currentSongIds={currentSongIds}
            onAddSong={addSongBlockFromSuggestion}
            onClose={() => setAiOpen(false)}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}


// ═══ PRESENT: navigate to the full Presenter Console at /worship/present/:id ═══
